/**
 * WebformErrorSummary
 *
 * Observes the Webform DOM for inline validation errors appearing.
 * When errors are detected (after a submit attempt), renders a
 * FormErrorSummary banner at the top and scrolls to it.
 *
 * Uses MutationObserver because the Webform library doesn't expose
 * a callback for client-side Formik validation failures.
 */

import React, {
  useEffect, useRef, useState, useCallback,
} from 'react';
import { useTranslation } from 'react-i18next';
import FormErrorSummary from './index';

interface WebformErrorSummaryProps {
  /** Ref to the webform wrapper element to observe */
  wrapperRef: React.RefObject<HTMLDivElement>;
  /** Whether the backend refused for the IP vote limit */
  ipLimitError?: boolean;
}

const WebformErrorSummary: React.FC<WebformErrorSummaryProps> = ({
  wrapperRef,
  ipLimitError = false,
}) => {
  const { t } = useTranslation();
  const [showErrors, setShowErrors] = useState(false);
  const observerRef = useRef<MutationObserver | null>(null);

  const checkForErrors = useCallback(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const hasErrors = wrapper.querySelectorAll('.FormElement__error').length > 0;
    setShowErrors(hasErrors);
  }, [wrapperRef]);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return undefined;

    observerRef.current = new MutationObserver(checkForErrors);
    observerRef.current.observe(wrapper, {
      childList: true,
      subtree: true,
    });

    return () => {
      observerRef.current?.disconnect();
    };
  }, [wrapperRef, checkForErrors]);

  // Client-side errors are always fresh, so they take precedence: a message
  // from a previous attempt never sits on top of current field errors.
  if (ipLimitError && !showErrors) {
    return (
      <FormErrorSummary
        errors={[String(t(
          'webform.errors.ip_limit',
          'Je stem kan niet worden geregistreerd, omdat de limiet voor het aantal stemmen per ip-adres is bereikt.',
        ))]}
        visible
        title={String(t(
          'form.error_summary_server_title',
          'Je inzending kon niet worden verwerkt:',
        ))}
      />
    );
  }

  return (
    <FormErrorSummary
      errors={[String(t(
        'form.error_summary_generic',
        'Controleer de gemarkeerde velden hieronder en probeer het opnieuw.',
      ))]}
      visible={showErrors}
    />
  );
};

export default WebformErrorSummary;
